import type { StatusBadgeVariant } from "@/components/shared/StatusBadge";

/** Mirrors the backend's QuoteRequestStatus union (quote-request.entity.ts). */
export type QuoteRequestStatus =
  | "draft"
  | "submitted"
  | "triaging"
  | "triaged"
  | "triage_failed"
  | "quoting"
  | "awarded"
  | "paid"
  | "fulfilling"
  | "completed"
  | "fulfillment_failed"
  | "expired"
  | "cancelled";

/** Mirrors the backend's QuoteRequestEventType union (quote-request-event.entity.ts). */
export type QuoteRequestEventType =
  | "submitted"
  | "triaged"
  | "triage_failed"
  | "broadcast"
  | "quote_submitted"
  | "quote_withdrawn"
  | "awarded"
  | "quote_rejected"
  | "checkout_created"
  | "paid"
  | "child_request_created"
  | "attachment_added"
  | "attachment_deleted"
  | "completed"
  | "fulfillment_failed"
  | "expired"
  | "cancelled";

/**
 * The consumer sees the real status. There is deliberately no masking layer
 * here any more (spec add326e0 §4d.1).
 *
 * `triage_failed` used to be rendered as "Clasificando tu solicitud…", on the
 * reasoning that the requester does not need to know an AI failed and a human
 * took over. The data showed what that actually costs: seven requests sat in
 * triage_failed for 8 to 31 days, every one of them displaying to its owner as
 * healthy and in progress. Someone whose request has been stuck for a month
 * cannot chase it if the screen insists it is fine, and the mask is what
 * turned a slow path into a silent one.
 *
 * If a status is unflattering, the fix is to fix the status, not to relabel it.
 */

/** Same keys as consumer list/detail badges; internal handoff statuses use plain-language labels. */
const QUOTE_STATUS_LABEL_FOR_DISPLAY: Record<QuoteRequestStatus, string> = {
  draft: "requests.user.quoteRequestStatusSubmitted",
  submitted: "requests.user.quoteRequestStatusSubmitted",
  triaging: "requests.user.quoteRequestStatusTriaging",
  // Honest, and deliberately not alarming: the consumer does not need the words
  // "AI" or "failed", but they do need to know a person is now looking at it —
  // which is both true and the thing that makes chasing it possible.
  triage_failed: "requests.user.quoteRequestStatusUnderReview",
  triaged: "requests.user.quoteRequestStatusTriaged",
  quoting: "requests.user.quoteRequestStatusQuoting",
  awarded: "requests.user.quoteRequestStatusAwarded",
  paid: "requests.user.quoteRequestStatusPaid",
  fulfilling: "requests.user.quoteRequestStatusFulfilling",
  completed: "requests.user.quoteRequestStatusCompleted",
  fulfillment_failed: "requests.user.quoteRequestStatusFulfillmentFailed",
  expired: "requests.user.quoteRequestStatusExpired",
  cancelled: "requests.user.quoteRequestStatusCancelled",
};

const QUOTE_VARIANT_FOR_DISPLAY: Record<QuoteRequestStatus, StatusBadgeVariant> = {
  draft: "warning",
  submitted: "warning",
  triaging: "warning",
  triage_failed: "warning",
  triaged: "info",
  quoting: "info",
  awarded: "info",
  paid: "info",
  fulfilling: "info",
  completed: "success",
  fulfillment_failed: "danger",
  expired: "softDanger",
  cancelled: "softDanger",
};

/**
 * Whether bidding has actually closed on a request still recorded as
 * `quoting`.
 *
 * The row stays `quoting` for the whole award grace period after
 * `quotingExpiresAt` — the expiry sweep only retires it once that lapses too.
 * During that window no professional can bid (the bidding endpoint gates on
 * this same timestamp), so showing "Recibiendo cotizaciones" tells the
 * consumer something that is not true, for up to 48 hours.
 */
export function isQuotingClosed(
  status: QuoteRequestStatus,
  legs: ReadonlyArray<{ quotingExpiresAt: string | null }>,
): boolean {
  if (status !== "quoting" || legs.length === 0) return false;
  // EVERY leg must have closed. A two-discipline request can have its escribano
  // leg shut while the contador leg is still taking bids, and "Recibiendo
  // cotizaciones" is still true then — the deadline is per leg, not per request.
  return legs.every((leg) => {
    if (!leg.quotingExpiresAt) return false;
    const closesAt = new Date(leg.quotingExpiresAt).getTime();
    return Number.isFinite(closesAt) && closesAt <= Date.now();
  });
}

/**
 * Whether bidding has closed on ONE leg.
 *
 * The per-leg counterpart of `isQuotingClosed`, which answers for the whole
 * request and requires EVERY leg to have shut. Here the question is per row:
 * one leg can be closed while its sibling still collects, and that is exactly
 * the case the request-level answer cannot express.
 *
 * Lives beside its request-level sibling rather than privately inside a card,
 * because two copies of one predicate is how the two scopes drift into
 * disagreeing about the same instant.
 */
export function isLegQuotingClosed(leg: {
  status: string;
  quotingExpiresAt: string | null;
}): boolean {
  if (leg.status !== "quoting" || leg.quotingExpiresAt === null) return false;
  const closesAt = new Date(leg.quotingExpiresAt).getTime();
  return Number.isFinite(closesAt) && closesAt <= Date.now();
}

/**
 * Whether ONE leg is still taking new bids.
 *
 * NOT `!isLegQuotingClosed(leg)`, which is the reading that looks obviously
 * equivalent and is wrong: that helper answers false for an AWARDED leg too,
 * because a leg that stopped quoting by being decided never "closed" in its
 * sense. Asking it the open question would therefore promise a consumer more
 * cotizaciones on a leg they have already chosen from.
 *
 * Lives beside its siblings for the reason stated on them: the email and the
 * page both tell a consumer when bids stop arriving, and two private copies of
 * this predicate is how the two start naming different days.
 */
export function isLegQuotingOpen(leg: {
  status: string;
  quotingExpiresAt: string | null;
}): boolean {
  if (leg.status !== "quoting" || leg.quotingExpiresAt === null) return false;
  const closesAt = new Date(leg.quotingExpiresAt).getTime();
  return Number.isFinite(closesAt) && closesAt > Date.now();
}

/**
 * The badge colour for ONE leg's status.
 *
 * Shared for the same reason as `isLegQuotingClosed`: the legs card and the
 * quotes section both label the same leg, and two private copies of this map
 * is how one surface starts calling a state amber while the other calls it
 * blue.
 */
export function legStatusBadgeVariant(status: string): StatusBadgeVariant {
  if (status === "completed") return "success";
  if (
    status === "fulfillment_failed" ||
    status === "cancelled" ||
    status === "expired"
  ) {
    return "danger";
  }
  if (status === "triaged" || status === "quoting") return "info";
  return "warning";
}

/** i18n key for the status badge (consumer-facing). */
export function getConsumerStatusTranslationKey(
  status: QuoteRequestStatus,
  legs: ReadonlyArray<{ quotingExpiresAt: string | null }> = [],
): string {
  if (isQuotingClosed(status, legs)) {
    return "requests.user.quoteRequestStatusQuotingClosed";
  }
  return QUOTE_STATUS_LABEL_FOR_DISPLAY[status];
}

/** Badge color for the consumer status chip. */
export function getConsumerStatusBadgeVariant(
  status: QuoteRequestStatus,
  legs: ReadonlyArray<{ quotingExpiresAt: string | null }> = [],
): StatusBadgeVariant {
  // Not danger: nothing has gone wrong, the request is simply between phases.
  if (isQuotingClosed(status, legs)) return "warning";
  return QUOTE_VARIANT_FOR_DISPLAY[status] ?? "info";
}

/**
 * Event visibility is NOT a client concern for RFQs and deliberately has no
 * helper here. A professional must never receive another professional's
 * quote_submitted event, so the filtering is a security boundary and lives
 * server-side in `apps/backend/src/auth/utils/quote-request-event-visibility.ts`
 * — the consumer and pro endpoints already return a filtered list. Do not
 * reintroduce a client-side filter: it would imply the raw events had been
 * shipped to the browser in the first place.
 */

/**
 * Statuses at which the consumer may still cancel an RFQ.
 *
 * Mirrors CANCELLABLE_STATUSES in apps/backend/src/auth/quote-request.service.ts
 * and must stay in lockstep with it. Cancellation is pre-payment only, per spec
 * 58787d44: once `paid` lands the money has moved and a child request may
 * already exist, so the exit is a refund path rather than a cancel.
 *
 * Deliberately an ALLOW-list, not a deny-list of terminal statuses. With a deny
 * list a newly added status defaults to "cancellable" on the client while the
 * server's allow-list rejects it — the consumer gets a Cancel button that
 * errors. Deny-by-default fails closed instead.
 *
 * This lives here, and not inline at each call site, precisely because it was
 * duplicated before: the list page tested the NOTARIAL statuses
 * ('pending' | 'cannot_complete'), which no RFQ status ever equals, so the
 * Cancel action silently never appeared on an RFQ row.
 */
const QUOTE_REQUEST_CANCELLABLE_STATUSES: readonly QuoteRequestStatus[] = [
  "draft",
  "submitted",
  "triaging",
  "triaged",
  "triage_failed",
  "quoting",
  "awarded",
];

export function isQuoteRequestCancellable(status: string): boolean {
  return QUOTE_REQUEST_CANCELLABLE_STATUSES.includes(status as QuoteRequestStatus);
}

/** The disciplines a collapsed broadcast row covers, in leg order. */
export type QuoteDiscipline = "notary" | "accountant";

/** The fields the broadcast collapse reads. Kept structural so the caller can
 *  pass either the service DTO or an already-mapped activity row. */
export interface CollapsibleQuoteEvent {
  eventType: string;
  createdAt: string;
  legId?: string | null;
}

/**
 * One dispatch, one row.
 *
 * A multi-discipline RFQ broadcasts each leg separately, so `broadcastLeg`
 * writes one `broadcast` event per leg. The consumer timeline renders every
 * event with the same label and deliberately suppresses the backend's
 * per-event message, so two legs produced two rows reading "Enviada a
 * profesionales" at the same second — correct data, indistinguishable render.
 *
 * The consumer does not care that dispatch is per-leg; they care that it went
 * out and to whom. So the run collapses to the FIRST event, carrying the
 * disciplines it covered, and the label names them.
 *
 * NOT collapsed: broadcasts far apart in time. A leg re-broadcast days later
 * is a genuinely separate event, and folding it into the original row would
 * backdate it. Only a run of broadcasts within `windowMs` of the first — one
 * dispatch — merges. Professionals need none of this: their visibility filter
 * already scopes leg events to the legs they are eligible for, so they never
 * see more than their own.
 */
export function collapseQuoteBroadcastEvents<T extends CollapsibleQuoteEvent>(
  events: readonly T[],
  legDisciplines: ReadonlyMap<string, QuoteDiscipline>,
  windowMs = 60_000,
): Array<T & { broadcastDisciplines?: QuoteDiscipline[] }> {
  const out: Array<T & { broadcastDisciplines?: QuoteDiscipline[] }> = [];

  for (let i = 0; i < events.length; i += 1) {
    const event = events[i];
    if (event.eventType !== "broadcast") {
      out.push(event);
      continue;
    }

    // Walk the run of broadcasts that belong to this dispatch.
    const runStart = Date.parse(event.createdAt);
    const disciplines: QuoteDiscipline[] = [];
    let j = i;
    while (j < events.length && events[j].eventType === "broadcast") {
      const at = Date.parse(events[j].createdAt);
      // An unparseable date must not silently extend the run.
      if (!Number.isFinite(runStart) || !Number.isFinite(at)) break;
      if (Math.abs(at - runStart) > windowMs) break;
      const legId = events[j].legId;
      const discipline = legId == null ? undefined : legDisciplines.get(legId);
      if (discipline !== undefined && !disciplines.includes(discipline)) {
        disciplines.push(discipline);
      }
      j += 1;
    }

    // j === i means the first broadcast itself failed the date check; keep it
    // as-is rather than dropping an event we could not reason about.
    const consumed = Math.max(j - i, 1);
    out.push(disciplines.length > 0 ? { ...event, broadcastDisciplines: disciplines } : event);
    i += consumed - 1;
  }

  return out;
}
